import { JobListResponse } from '@doda/api-client/aspida/@types';
import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { FetchBaseStateType } from '@doda/common/src/interfaces/fetchBaseState';
import { LOADING_STATUS } from '@doda/common/src/constants/loadingStatus';
import {
  fetchJobList,
  fetchSaveSearchesJobList,
} from './services/fetchJobList';

/**
 * ジョブリストの状態を示すインターフェース
 * @interface JobListState
 */
interface JobListState {
  /** ジョブリストのフェッチ状態 */
  jobList: FetchBaseStateType<JobListResponse | null>;
  /** バリデーションメッセージの配列 */
  validationMessages: string[];
}

/**
 * ジョブリストの初期状態
 * @const
 * @type {JobListState}
 */
export const initialState: JobListState = {
  jobList: {
    loading: LOADING_STATUS.IDLE,
    entity: null,
  },
  validationMessages: [],
};

/**
 * ジョブリストのスライスを作成
 * @const
 * @type {ReturnType<typeof createSlice>}
 */
const jobListSlice = createSlice({
  name: 'jobList',
  initialState,
  reducers: {
    /**
     * バリデーションメッセージを設定するアクション
     * @param {JobListState} state - 現在の状態
     * @param {PayloadAction<string[]>} payload - 新しいバリデーションメッセージ
     */
    setValidationMessages: (state, { payload }: PayloadAction<string[]>) => {
      state.validationMessages = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchJobList.pending, (state) => {
      state.jobList.loading = LOADING_STATUS.PENDING;
    });
    builder.addCase(fetchJobList.fulfilled, (state, action) => {
      state.jobList.loading = LOADING_STATUS.SUCCESS;
      state.jobList.entity = action.payload;
    });
    builder.addCase(fetchJobList.rejected, (state) => {
      state.jobList.loading = LOADING_STATUS.ERROR;
    });
    builder.addCase(fetchSaveSearchesJobList.pending, (state) => {
      state.jobList.loading = LOADING_STATUS.PENDING;
    });
    builder.addCase(fetchSaveSearchesJobList.fulfilled, (state, action) => {
      state.jobList.loading = LOADING_STATUS.SUCCESS;
      state.jobList.entity = action.payload;
    });
    builder.addCase(fetchSaveSearchesJobList.rejected, (state) => {
      state.jobList.loading = LOADING_STATUS.ERROR;
    });
  },
});

/** バリデーションメッセージを設定するアクション */
export const { setValidationMessages } = jobListSlice.actions;

/** ジョブリストのリデューサー */
export const jobListReducer = jobListSlice.reducer;

/**
 * ルートステートを示すインターフェース
 * @interface RootState
 */
interface RootState {
  jobList: JobListState;
}

/**
 * ジョブリストのエンティティセレクター
 * @param {RootState} state - ルートステート
 * @returns {JobListResponse | null} ジョブリストのエンティティ
 */
export const jobListEntiySelector = (state: RootState) =>
  state.jobList.jobList.entity;

const EMPTY_ARRAY = [];

/**
 * ジョブリストセレクター
 * @param {RootState} state - ルートステート
 * @returns {Array} ジョブリストの結果配列または空配列
 */
export const jobListSelector = (state: RootState) =>
  state.jobList.jobList.entity?.results || EMPTY_ARRAY;

/**
 * メタデータセレクター
 * @param {RootState} state - ルートステート
 * @returns {Object} ジョブリストのメタデータまたはundefined
 */
export const MetaDataSelector = (state: RootState) =>
  state.jobList.jobList.entity?.metaData;

/**
 * ジョブリストの総数セレクター
 * @param {any} state - ルートステート
 * @returns {number} ジョブリストの総数または0
 */
export const totalJobCountSelector = (state: RootState): number =>
  state.jobList.jobList.entity?.metaData?.totalCount || 0;

/**
 * 結果カウントセレクター
 * @param {RootState} state - ルートステート
 * @returns {number} 結果カウントまたは0
 */
export const resultCountSelector = (state: RootState) =>
  state.jobList.jobList.entity?.metaData?.resultCount || 0;

/**
 * 動画求人の数セレクター
 * @param {Array} jobList - ジョブリストの結果配列
 * @returns {number} 動画求人の数
 */
export const numOfVideoSelector = createSelector(jobListSelector, (jobList) => {
  if (!jobList || jobList.length === 0) return 0;
  return jobList.filter((job) => job.movieId).length;
});

/**
 * バリデーションメッセージセレクター
 * @param {any} state - ルートステート
 * @returns {Object} バリデーションメッセージと可視性オブジェクト
 */
export const validationMessagesSelector = createSelector(
  (state: RootState) => state.jobList.validationMessages,
  (validationMessages) => {
    const messages: string[] = Array.isArray(validationMessages)
      ? validationMessages
      : [];
    const isVisibleValidationMessages = messages.length !== 0;
    return {
      isVisibleValidationMessages,
      validationMessages: messages,
    };
  }
);
